import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

import * as LucideIcons from "lucide-react";
import AgentService from "../../services/api/agents";
import SCMService from "../../services/api/sourceCodeManagement";
import { showNotificationGlobal } from "../common/NotificationProvider";
import { validBranchNameRegex } from "../../settings/shared/constants";

// ─── Design tokens ────────────────────────────────────────────────────────
// The accent ties into the product's own violet brand color (the active
// "Frontend" tab pill) instead of a generic blue. Status colors get their
// own named tokens so they're used consistently rather than ad-hoc greys.

const C = {
  ink: "#1B1023",
  secondary: "#655F73",
  muted: "#9C96AC",
  border: "#E9E4F1",
  borderStrong: "#D6CDE3",
  canvas: "#FAFAFB",
  surface: "#ffffff",

  accent: "#6E56CF",
  accentHover: "#5A46B0",
  accentTint: "#F2EEFC",
  accentSoft: "#E3DBF7",

  scmMark: "#181121",

  green: "#1B8F5C",
  greenTint: "#E9F7F0",

  amber: "#AD7A0C",
  amberTint: "#FBF1DE",

  coral: "#C1403F",
  coralTint: "#FBEAEA",

  cellBg: "#F7F5FC",
  cellGutter: "#EFE9FA",
};

const AVATAR_PALETTE = ["#6E56CF", "#1B8F5C", "#AD7A0C", "#C1403F", "#2A6FB0"];

function colorForName(name) {
  if (!name) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

// Mirrors SCMConnectionServiceImpl.validateBranch on the backend: non-empty
// after trim, and matches the same VALID_BRANCH_NAME pattern (imported from
// constants.js so there's exactly one copy of the regex on the frontend).
// Catching this client-side means a bad name never round-trips to the
// server just to bounce back with EC_5124.
function isValidBranchName(name) {
  const trimmed = name.trim();
  return trimmed.length > 0 && validBranchNameRegex.test(trimmed);
}

const FONT =
  "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif";

const MONO =
  "'SFMono-Regular', ui-monospace, 'JetBrains Mono', Menlo, monospace";

const Styles = {
  page: {
    minHeight: "100%",
    background: C.canvas,
    fontFamily: FONT,
    color: C.ink,
    overflowY: "auto",
  },

  topBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "16px 32px",
    borderBottom: `1px solid ${C.border}`,
    background: C.surface,
  },

  backButton: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    border: "none",
    background: "transparent",
    color: C.secondary,
    fontSize: 13,
    cursor: "pointer",
    padding: "4px 6px",
    borderRadius: 6,
  },

  breadcrumb: {
    fontSize: 13,
    color: C.muted,
  },

  content: {
    margin: "0 auto",
    padding: "40px 24px 80px",
  },

  pageHeader: {
    marginBottom: 22,
  },

  pageTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: C.ink,
    letterSpacing: "-0.01em",
  },

  pageSubtitle: {
    fontSize: 14,
    color: C.secondary,
    marginTop: 4,
    lineHeight: 1.5,
  },

  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
  },

  section: {
    padding: "22px 26px",
  },

  sectionDivider: {
    height: 1,
    background: C.border,
    margin: "0 26px",
  },

  lockupWrap: (roomy) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: roomy ? "34px 20px 8px" : "20px 20px 4px",
  }),

  lockupBadge: (bg, fg) => ({
    width: 46,
    height: 46,
    borderRadius: 13,
    background: bg,
    color: fg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  lockupTrack: {
    position: "relative",
    flex: 1,
    minWidth: 64,
    maxWidth: 190,
    height: 4,
    borderRadius: 4,
    overflow: "hidden",
  },

  lockupTrackDashed: (color) => ({
    position: "absolute",
    inset: 0,
    backgroundImage: `repeating-linear-gradient(90deg, ${color} 0px, ${color} 7px, transparent 7px, transparent 13px)`,
  }),

  lockupTrackSolid: (color) => ({
    position: "absolute",
    inset: 0,
    background: color,
  }),

  lockupComet: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "42%",
    borderRadius: 4,
    background: `linear-gradient(90deg, transparent, ${C.accent}, transparent)`,
    animation: "sax-scm-comet 1.5s ease-in-out infinite",
  },

  lockupLabelsRow: {
    display: "flex",
    justifyContent: "space-between",
    maxWidth: 262,
    margin: "10px auto 0",
  },

  lockupLabel: {
    fontSize: 11.5,
    color: C.muted,
    fontWeight: 500,
    width: 46,
    textAlign: "center",
  },

  lockupCaption: {
    textAlign: "center",
    marginTop: 16,
  },

  repoRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  repoIdentity: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  repoAvatarWrap: {
    position: "relative",
    flexShrink: 0,
  },

  repoAvatar: (bg) => ({
    width: 40,
    height: 40,
    borderRadius: 10,
    background: bg,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
  }),

  avatarStatusDot: (color) => ({
    position: "absolute",
    bottom: -2,
    right: -2,
    width: 13,
    height: 13,
    borderRadius: "50%",
    background: color,
    border: `2px solid ${C.surface}`,
  }),

  repoName: {
    fontSize: 14.5,
    fontWeight: 600,
    color: C.ink,
    fontFamily: MONO,
  },

  visibilityBadge: {
    fontSize: 11,
    fontWeight: 500,
    color: C.secondary,
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    padding: "1px 7px",
    marginLeft: 8,
    verticalAlign: "middle",
    textTransform: "capitalize",
  },

  externalLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12.5,
    color: C.accent,
    textDecoration: "none",
    fontWeight: 500,
    marginTop: 4,
  },

  syncTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: C.ink,
  },

  syncSubtitle: {
    fontSize: 13,
    color: C.secondary,
    marginTop: 2,
    lineHeight: 1.5,
  },

  syncTimestamp: {
    fontSize: 12,
    color: C.muted,
    marginTop: 4,
  },

  iconGhostButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    border: `1px solid ${C.border}`,
    background: C.surface,
    color: C.secondary,
    fontSize: 12.5,
    fontWeight: 500,
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: 7,
    flexShrink: 0,
  },

  textGhostButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    border: "none",
    background: "transparent",
    color: C.accent,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    padding: "4px 6px",
  },

  sectionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: C.ink,
    marginBottom: 12,
  },

  tabRow: {
    display: "flex",
    gap: 18,
    borderBottom: `1px solid ${C.border}`,
    marginBottom: 12,
  },

  tabButton: (active) => ({
    border: "none",
    background: "transparent",
    color: active ? C.ink : C.muted,
    fontSize: 12.5,
    fontWeight: 500,
    padding: "0 0 8px",
    marginBottom: -1,
    borderBottom: active ? `2px solid ${C.accent}` : "2px solid transparent",
    cursor: "pointer",
  }),

  cell: (accentColor = C.accent) => ({
    display: "flex",
    alignItems: "stretch",
    border: `1px solid ${C.border}`,
    borderLeft: `3px solid ${accentColor}`,
    borderRadius: 8,
    background: C.cellBg,
    overflow: "hidden",
  }),

  cellGutter: {
    width: 40,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: C.cellGutter,
    borderRight: `1px solid ${C.border}`,
    color: C.secondary,
    fontFamily: MONO,
    fontSize: 11,
  },

  cellBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
  },

  cellText: {
    fontFamily: MONO,
    fontSize: 12.5,
    color: C.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  copyButton: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 6,
    borderRadius: 6,
    display: "flex",
    color: C.muted,
    flexShrink: 0,
  },

  actionsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  actionHint: {
    fontSize: 12.5,
    color: C.muted,
    marginTop: 10,
  },

  configRow: (selected) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "13px 14px",
    borderRadius: 10,
    border: `1px solid ${selected ? C.accent : C.border}`,
    borderLeft: `3px solid ${selected ? C.accent : "transparent"}`,
    marginBottom: 8,
    cursor: "pointer",
    background: selected ? C.accentTint : C.surface,
    transition: "border-color 0.12s, background 0.12s",
  }),

  radio: (selected) => ({
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: `1.5px solid ${selected ? C.accent : C.borderStrong}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  radioDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: C.accent,
  },

  errorBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "12px 14px",
    borderRadius: 10,
    background: C.coralTint,
    color: C.coral,
    fontSize: 13,
    marginBottom: 20,
  },

  skeletonRow: (w) => ({
    height: 14,
    width: w,
    borderRadius: 4,
    background:
      "linear-gradient(90deg, #F0EDF7 25%, #FAF8FD 37%, #F0EDF7 63%)",
    backgroundSize: "400% 100%",
    animation: "sax-scm-shimmer 1.4s ease infinite",
  }),

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(27, 16, 35, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  modal: {
    width: 420,
    maxWidth: "calc(100vw - 32px)",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    boxShadow: "0 24px 60px rgba(27, 16, 35, 0.18)",
    overflow: "hidden",
  },

  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    padding: "18px 20px",
    borderBottom: `1px solid ${C.border}`,
  },

  modalTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: C.ink,
  },

  modalSubtitle: {
    margin: "5px 0 0",
    fontSize: 13,
    color: C.secondary,
    lineHeight: 1.5,
  },

  modalClose: {
    border: "none",
    background: "transparent",
    color: C.muted,
    cursor: "pointer",
    padding: 4,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  modalBody: {
    padding: 20,
  },

  sourceRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 10,
  },

  sourceLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: C.secondary,
  },

  sourceBranch: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 13,
    fontWeight: 600,
    color: C.ink,
    fontFamily: MONO,
  },

  inputLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: C.ink,
    marginBottom: 7,
  },

  branchInput: {
    width: "100%",
    boxSizing: "border-box",
    height: 40,
    padding: "0 12px",
    border: `1px solid ${C.borderStrong}`,
    borderRadius: 8,
    outline: "none",
    fontSize: 14,
    color: C.ink,
    background: C.surface,
    fontFamily: MONO,
  },

  inputHint: {
    margin: "7px 0 0",
    fontSize: 12,
    color: C.muted,
  },

  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "14px 20px",
    borderTop: `1px solid ${C.border}`,
    background: C.canvas,
  },

  branchMenuWrap: {
    position: "relative",
    display: "inline-flex",
  },

  branchMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    width: 220,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(27, 16, 35, 0.16)",
    overflow: "hidden",
    zIndex: 50,
    animation: "sax-scm-fade-in 0.12s ease",
  },

  branchMenuLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: C.muted,
    padding: "10px 12px 6px",
  },

  branchMenuList: {
    maxHeight: 180,
    overflowY: "auto",
    padding: "0 4px",
  },

  branchMenuItem: (selected, disabled) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    boxSizing: "border-box",
    border: "none",
    background: selected ? C.accentTint : "transparent",
    color: disabled ? C.muted : C.ink,
    fontSize: 13,
    fontFamily: MONO,
    padding: "7px 8px",
    borderRadius: 6,
    cursor: disabled ? "not-allowed" : "pointer",
    textAlign: "left",
    opacity: disabled ? 0.6 : 1,
  }),

  branchMenuDivider: {
    height: 1,
    background: C.border,
    margin: "6px 0",
  },

  branchMenuCreate: (disabled) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    boxSizing: "border-box",
    border: "none",
    background: "transparent",
    color: disabled ? C.muted : C.accent,
    fontSize: 13,
    fontWeight: 500,
    padding: "10px 12px",
    cursor: disabled ? "not-allowed" : "pointer",
    textAlign: "left",
    opacity: disabled ? 0.6 : 1,
  }),
};

const globalKeyframes = `

@keyframes sax-scm-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }

}

@keyframes sax-scm-spin {
  to { transform: rotate(360deg); }
}

@keyframes sax-scm-line-flow {
  from {transform: translateY(-100%);}
  to {transform: translateY(100%);}
}

@keyframes sax-scm-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes sax-scm-card-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes sax-scm-comet {
  0% { transform: translateX(-130%); }
  100% { transform: translateX(240%); }
}
`;

function formatRelativeTime(date) {
  if (!date) return null;

  const seconds = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / 1000)
  );

  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function Btn({ variant = "neutral", disabled, onClick, children, title }) {
  const [hover, setHover] = useState(false);

  const palette = {
    primary: { bg: C.accent, bgHover: C.accentHover, fg: "#fff", border: "transparent" },
    neutral: { bg: C.surface, bgHover: C.canvas, fg: C.ink, border: C.border },
    danger: { bg: C.surface, bgHover: C.coralTint, fg: C.coral, border: C.border },
  }[variant];

  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        padding: "9px 15px",
        borderRadius: 8,
        border: `1px solid ${palette.border}`,
        background: disabled ? palette.bg : hover ? palette.bgHover : palette.bg,
        color: palette.fg,
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "background 0.15s",
      }}
    >
      {children}
    </button>
  );
}

function ConnectionLockup({ phase, roomy = true }) {
  return (
    <div style={Styles.lockupWrap(roomy)}>
      <div style={Styles.lockupBadge(C.accent, "#fff")}>
        <LucideIcons.Layers size={19} />
      </div>

      <div style={Styles.lockupTrack}>
        {phase === "connected" && (
          <div style={Styles.lockupTrackSolid(C.green)} />
        )}

        {phase === "pending" && (
          <>
            <div style={Styles.lockupTrackDashed(C.accentSoft)} />
            <div style={Styles.lockupComet} />
          </>
        )}

        {phase === "disconnected" && (
          <div style={Styles.lockupTrackDashed(C.borderStrong)} />
        )}
      </div>

      <div style={Styles.lockupBadge(C.scmMark, "#fff")}>
        <LucideIcons.Github size={19} />
      </div>
    </div>
  );
}

function CloneCell({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showNotificationGlobal("Couldn't copy to clipboard", "error", "bottomRight");
    }
  };

  if (!text) return null;

  return (
    <div style={Styles.cell(C.accent)}>
      <div style={Styles.cellGutter}>git</div>
      <div style={Styles.cellBody}>
        <span style={Styles.cellText}>{text}</span>
        <button style={Styles.copyButton} onClick={handleCopy} title="Copy">
          {copied ? (
            <LucideIcons.Check size={14} color={C.green} />
          ) : (
            <LucideIcons.Copy size={14} />
          )}
        </button>
      </div>
    </div>
  );
}

function StatusCell({ synced, branch }) {
  const color = synced ? C.green : C.amber;

  const line = synced
    ? `On branch ${branch} — up to date with origin/${branch}`
    : `On branch ${branch} — not yet pushed to origin/${branch}`;

  return (
    <div style={Styles.cell(color)}>
      <div style={{ ...Styles.cellGutter, color }}>
        {synced ? <LucideIcons.Check size={14} /> : <LucideIcons.ArrowUpFromLine size={13} />}
      </div>
      <div style={Styles.cellBody}>
        <span style={Styles.cellText}>{line}</span>
      </div>
    </div>
  );
}
function SyncProgressCell({ steps }) {
  if (!steps.length) {
    return null;
  }

  return (
    <div
      style={{
        marginTop: 12,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {steps.map((step, index) => {
        const isActive = step.status === "processing";
        const isLast = index === steps.length - 1;

        return (
          <div
            key={`${step.label}-${index}`}
            style={{
              display: "flex",
              minHeight: isLast ? 20 : 38,
            }}
          >
            <div
              style={{
                width: 18,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {isActive ? (
                <LucideIcons.Loader2
                  size={15}
                  color={C.accent}
                  strokeWidth={2}
                  style={{
                    animation: "sax-scm-spin 0.8s linear infinite",
                  }}
                />
              ) : (
                <LucideIcons.Check
                  size={15}
                  color={C.green}
                  strokeWidth={2.5}
                />
              )}

              {!isLast && (
                <div
                  style={{
                    position: "relative",
                    width: 1.5,
                    flex: 1,
                    minHeight: 18,
                    marginTop: 4,
                    background: C.borderStrong,
                    overflow: "hidden",
                  }}
                >
                  {isActive && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-100%",
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: `linear-gradient(
                          to bottom,
                          transparent,
                          ${C.accent},
                          transparent
                        )`,
                        animation:
                          "sax-scm-line-flow 1s linear infinite",
                      }}
                    />
                  )}
                </div>
              )}

              {isLast && !isActive && null}
            </div>

            <div
              style={{
                marginLeft: 10,
                fontFamily: MONO,
                fontSize: 12.5,
                lineHeight: "18px",
                color: isActive ? C.ink : C.secondary,
                fontWeight: isActive ? 500 : 400,
              }}
            >
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function deriveCloneVariants(cloneUrl, fullName) {
  if (!cloneUrl) {
    return { https: null, ssh: null, cli: null };
  }

  let ownerRepo = fullName;

  if (!ownerRepo) {
    const match = cloneUrl.match(/github\.com[:/]([^/]+\/[^/.]+)(?:\.git)?$/i);
    ownerRepo = match ? match[1] : null;
  }

  return {
    https: `git clone ${cloneUrl}`,
    ssh: ownerRepo ? `git clone git@github.com:${ownerRepo}.git` : null,
    cli: ownerRepo ? `gh repo clone ${ownerRepo}` : null,
  };
}

function GitHubConfigRow({ app, selected, onClick, onConnect, connecting }) {
  return (
    <div style={Styles.configRow(selected)} onClick={onClick}>
      <div style={Styles.radio(selected)}>
        {selected && <div style={Styles.radioDot} />}
      </div>

      <LucideIcons.Github size={16} color={C.secondary} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: C.ink }}>
          {app.name || "GitHub configuration"}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>
          {app.provider || "GITHUB"}
        </div>
      </div>

      {selected && (
        <Btn
          variant="primary"
          disabled={connecting}
          onClick={(e) => {
            e.stopPropagation();
            onConnect(app);
          }}
        >
          {connecting ? "Connecting…" : "Connect"}
        </Btn>
      )}
    </div>
  );
}

// Branch dropdown, wired to actually switch branches: picking a non-active
// branch calls onSelectBranch(branch), which the page turns into a
// SCMService.switchBranch(...) call. `switching` disables the trigger and
// every item while that request is in flight, and swaps the chevron for a
// spinner so it's clear something's happening rather than looking
// unresponsive.
function BranchDropdown({ branches, activeBranch, onCreateBranch, onSelectBranch, switching }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const list = branches && branches.length ? branches : [activeBranch];

  return (
    <div style={Styles.branchMenuWrap} ref={wrapRef}>
      <button
        type="button"
        style={Styles.iconGhostButton}
        onClick={() => setOpen((o) => !o)}
        disabled={switching}
        title={switching ? "Switching branch…" : "Switch branch"}
      >
        <LucideIcons.GitBranch size={13} />
        <span style={{ fontFamily: MONO }}>{activeBranch}</span>
        {switching ? (
          <LucideIcons.Loader2
            size={13}
            style={{ animation: "sax-scm-spin 0.8s linear infinite" }}
          />
        ) : (
          <LucideIcons.ChevronDown size={13} />
        )}
      </button>

      {open && (
        <div style={Styles.branchMenu}>
          <div style={Styles.branchMenuLabel}>Branches</div>
          <div style={Styles.branchMenuList}>
            {list.map((branch) => {
              const isActive = branch === activeBranch;
              return (
                <button
                  key={branch}
                  type="button"
                  style={Styles.branchMenuItem(isActive, switching)}
                  disabled={switching}
                  onClick={() => {
                    setOpen(false);
                    if (!isActive) {
                      onSelectBranch(branch);
                    }
                  }}
                >
                  {isActive && <LucideIcons.Check size={13} color={C.accent} />}
                  {branch}
                </button>
              );
            })}
          </div>
          <div style={Styles.branchMenuDivider} />
          <button
            type="button"
            style={Styles.branchMenuCreate(switching)}
            disabled={switching}
            onClick={() => {
              setOpen(false);
              onCreateBranch();
            }}
          >
            <LucideIcons.GitBranchPlus size={13} />
            Create branch
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────

function SCMIntegrationPage({ projectId, projectName, onBack }) {
  const [githubApps, setGithubApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);

  const [gitMapping, setGitMapping] = useState(null);

  const [loading, setLoading] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [fetchingBranches, setFetchingBranches] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncSteps, setSyncSteps] = useState([]);

  const [error, setError] = useState("");
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const [cloneTab, setCloneTab] = useState("https");

  const [isCreateBranchOpen, setIsCreateBranchOpen] = useState(false);
  const [branchName, setBranchName] = useState("");
  const [branchNameError, setBranchNameError] = useState("");
  const [creatingBranch, setCreatingBranch] = useState(false);
  

  const [isCommitMessageOpen, setIsCommitMessageOpen] = useState(false);
  const [commitMessage, setCommitMessage] = useState("");

  const [showRenamePopup, setShowRenamePopup] = useState(false);
  const [repositoryName, setRepositoryName] = useState("");

  // Branch switching lives alongside the other in-flight flags (syncing,
  // creatingBranch) so it can be composed with them below — the dropdown
  // is disabled if ANY of the three is happening, not just its own.
  const [switchingBranch, setSwitchingBranch] = useState(false);
  const lastShownMappingError = useRef("");

  const syncStartedAt = useRef(0);
  const MIN_SYNC_VISIBLE_MS = 3000;
  const loadGitHubConfigurations = useCallback(async () => {
    try {
      const response = await AgentService.getConnectedApps("GITHUB");
      const apps =
        response?.thirdPartyAppsDTOPage?.content || response?.content || [];
      setGithubApps(apps);
    } catch (err) {
      console.error("Failed to load GitHub configurations:", err);
      setError(
        err?.response?.data?.message || "Couldn't load your GitHub configurations."
      );
    }
  }, []);

  const loadProjectSCMMapping = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) {
          setRechecking(true);
        } else {
          setLoading(true);
        }

        setError("");

        const response = await SCMService.getProjectSCMMapping(projectId);
        const mapping = response?.data || response;

        setGitMapping(mapping);
        setLastCheckedAt(new Date());

        if (!mapping || mapping.mappingStatus === "FAILED" || !mapping.mappingStatus) {
          await loadGitHubConfigurations();
        }

        return mapping;
      } catch (err) {
        console.warn("Git mapping fetch failed:", err?.message);

        if (!silent) {
          setGitMapping(null);
          await loadGitHubConfigurations();
        }

        setError(silent ? "Re-check failed. Try again." : "");
      } finally {
        setLoading(false);
        setRechecking(false);
      }
    },
    [projectId, loadGitHubConfigurations]
  );

  useEffect(() => {
    if (!projectId) return;

    loadProjectSCMMapping();

    const handleGitMessage = async (event) => {
      const message = event?.detail || {};
      const ninfo =
        message?.nInfo ||
        message?.ninfo ||
        message;
      
      if (
        String(ninfo?.projectId || "").trim() !==
        String(projectId)
      ) {
        return;
      }

      const action = String(ninfo?.action || "").toUpperCase();

      if (
        action.includes("CHECKOUT") ||
        action.includes("BRANCH")
      ) {
        await loadProjectSCMMapping({ silent: true });
        setSwitchingBranch(false);
        setFetchingBranches(false);
        return;
      }

      const progressMessage = String(
        ninfo?.message || ""
      ).trim();

      if (!progressMessage) {
        return;
      }

      if (progressMessage === "Status Modified") {
        const mapping = await loadProjectSCMMapping({
          silent: true,
        });

        if (mapping?.synced) {
          setSyncSteps((currentSteps) =>
            currentSteps.map((step) => ({
              ...step,
              status: "done",
            }))
          );

          const elapsed =
            Date.now() - syncStartedAt.current;

          const wait = Math.max(
            0,
            MIN_SYNC_VISIBLE_MS - elapsed
          );

          setTimeout(() => {
            setSyncSteps([]);
            setSyncing(false);
          }, wait);
        }

        return;
      }

      setSyncing(true);

      setSyncSteps((currentSteps) => {
        const lastStep =
          currentSteps[currentSteps.length - 1];

        if (lastStep?.label === progressMessage) {
          return currentSteps;
        }

        return [
          ...currentSteps.map((step) => ({
            ...step,
            status: "done",
          })),
          {
            label: progressMessage,
            status: "processing",
          },
        ];
      });
    };

    window.addEventListener(
      "scmMessageReceived",
      handleGitMessage
    );

    return () => {
      window.removeEventListener(
        "scmMessageReceived",
        handleGitMessage
      );
    };
  }, [projectId, loadProjectSCMMapping]);

  const effectiveMapping = gitMapping;
  const repository = effectiveMapping?.repository;

  const gitStatus = repository?.gitStatus;
  const gitChanges = gitStatus?.changes || [];
  const gitChangeCount =
    gitStatus?.change_count ?? gitChanges.length;

  const isConnected = effectiveMapping?.mappingStatus === "CONNECTED";
  const isPending = effectiveMapping?.mappingStatus === "PENDING";
  const showPicker = !isConnected && !isPending;
  const mappingError = effectiveMapping?.errorMessage || "";

  useEffect(() => {
    if (!mappingError || isConnected) {
      lastShownMappingError.current = "";
      return;
    }

    if (lastShownMappingError.current === mappingError) {
      return;
    }

    lastShownMappingError.current = mappingError;

    showNotificationGlobal(
      mappingError,
      "error",
      "bottomRight"
    );
  }, [mappingError, isConnected]);

  const isSynced = !!effectiveMapping?.synced;

  const activeBranch =
    repository?.activeBranch || repository?.defaultBranch || "main";

  const branchList = repository?.branches || [activeBranch];

  const ownerLabel = repository?.owner || repository?.fullName || "?";
  const ownerInitial = ownerLabel.trim().charAt(0).toUpperCase();
  const avatarColor = useMemo(() => colorForName(ownerLabel), [ownerLabel]);

  const cloneVariants = useMemo(
    () => deriveCloneVariants(repository?.cloneUrl, repository?.fullName),
    [repository?.cloneUrl, repository?.fullName]
  );

  const hasCloneOptions =
    cloneVariants.https || cloneVariants.ssh || cloneVariants.cli;

  const activeCloneText = cloneVariants[cloneTab];

  const handleConnect = async (appArg) => {
    const app = appArg || selectedApp;
    if (!app || !projectId) return;

    //TOdo validate project Name
    const isUntitled =
      !projectName ||
      projectName.trim().toLowerCase() === "untitled app";

    if (isUntitled) {
      setShowRenamePopup(true);
      return;
    }
    // connect scm
    await connectSCM(app);
  };


  const connectSCM = async (appArg) => {
    const app = appArg || selectedApp;
    if (!app) return;

    try {
      setConnecting(true);
      setError("");

      await SCMService.connect({
        projectId,
        thirdPartyAppId: app.id,
      });

      showNotificationGlobal(
        "Connecting repository — this can take a moment.",
        "info",
        "bottomRight"
      );

      // The mapping now flips to PENDING server-side; this refresh picks
      // that up so the page moves straight into the provisioning view
      // instead of bouncing back to the account picker.
      await loadProjectSCMMapping({ silent: true });
    } catch (err) {
      console.error("GitHub connection failed:", err);
      setError(err?.response?.data?.message || "Couldn't connect to GitHub.");
    } finally {
      setConnecting(false);
    }
    
  };
  const handleRenameAndConnect = async () => {
    const newName = repositoryName.trim();

    if (!newName || !selectedApp || !projectId) return;

    try {
      setConnecting(true);
      setError("");

      // Existing project rename/update API
      await AgentService.updateProject(projectId, {
        name: newName,
      });
      setShowRenamePopup(false);
      setRepositoryName("");
      // Call connect scm
      await connectSCM(selectedApp);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        "Couldn't rename project or connect to GitHub."
      );
    } finally {
      setConnecting(false);
    }
  };

  const openCommitMessageModal = () => {
    setCommitMessage("");
    setIsCommitMessageOpen(true);
  };

  const closeCommitMessageModal = () => {
    if (syncing) return;
    setCommitMessage("");
    setIsCommitMessageOpen(false);
  };

  const handleSync = async () => {
    if (!projectId) return;

    const message = commitMessage.trim();

    if (!message) {
      showNotificationGlobal(
        "Please enter a commit message",
        "error",
        "bottomRight"
      );
      return;
    }

    try {
      syncStartedAt.current = Date.now();
      setSyncSteps([]);
      setSyncing(true);
      setCommitMessage("");
      setIsCommitMessageOpen(false);

      await SCMService.syncCode({
        projectId,
        commitMessage: message,
      });
    } catch (err) {
      console.error("Sync failed:", err);

      setSyncSteps([]);
      setSyncing(false);

      showNotificationGlobal(
        err?.response?.data?.message || "Couldn't sync code.",
        "error",
        "bottomRight"
      );
    }
  };
  const openCreateBranchModal = () => {
    setBranchName("");
    setBranchNameError("");
    setIsCreateBranchOpen(true);
  };

  const closeCreateBranchModal = () => {
    if (creatingBranch) return;
    setBranchName("");
    setBranchNameError("");
    setIsCreateBranchOpen(false);
  };

  // Validates as the person types, matching the backend's regex, so the
  // hint text and the Create button's disabled state stay in sync with
  // what handleCreateBranch will actually accept.
  const handleBranchNameChange = (value) => {
    setBranchName(value);

    if (!value.trim()) {
      setBranchNameError("");
      return;
    }

    setBranchNameError(
      isValidBranchName(value) ? "" : "Not a valid Git branch name."
    );
  };

  const handleCreateBranch = async () => {
    const name = branchName.trim();

    if (!name) {
      showNotificationGlobal(
        "Please enter a branch name",
        "error",
        "bottomRight"
      );
      return;
    }

    if (!isValidBranchName(name)) {
      setBranchNameError("Not a valid Git branch name.");
      return;
    }

    if (!projectId) {
      showNotificationGlobal(
        "Project ID is missing",
        "error",
        "bottomRight"
      );
      return;
    }

    const branchExists = branchList.some(
      (branch) => branch === name
    );

    if (branchExists) {
      showNotificationGlobal(
        `Branch "${name}" already exists in this repository.`,
        "error",
        "bottomRight"
      );
      return;
    }

    try {
      setCreatingBranch(true);

      await SCMService.createBranch({
        projectId,
        branchName: name,
      });

      showNotificationGlobal(
        `Branch "${name}" creation started`,
        "info",
        "bottomRight"
      );

      setBranchName("");
      setBranchNameError("");
      setIsCreateBranchOpen(false);

      await loadProjectSCMMapping({ silent: true });
    } catch (err) {
      showNotificationGlobal(
        err?.response?.data?.message ||
        "Couldn't create branch.",
        "error",
        "bottomRight"
      );
    } finally {
      setCreatingBranch(false);
    }
  };

  // Switching branches: same shape as handleSync/handleCreateBranch —
  // call the service, toast, then refresh the mapping (silent, so the
  // card doesn't unmount back to a skeleton). Bails out early if a
  // switch is already in flight so double-clicks can't fire twice.
 
  const handleSwitchBranch = async (branchName) => {
    if (!projectId || switchingBranch) return;

    try {
      setSwitchingBranch(true);

      await SCMService.switchBranch({
        projectId,
        branchName,
      });

      showNotificationGlobal(
        `Checkout requested for "${branchName}". Waiting for confirmation.`,
        "info",
        "bottomRight"
      );
    } catch (err) {
      console.error("Checkout branch request failed:", err);

      showNotificationGlobal(
        err?.response?.data?.message ||
        "Couldn't request branch checkout.",
        "error",
        "bottomRight"
      );

      setSwitchingBranch(false);
    }
  };


  const handleRecheck = () => loadProjectSCMMapping({ silent: true });

  const branchControlsBusy = switchingBranch || syncing || creatingBranch;

  const handleFetchBranches = async () => {
    if (!projectId || fetchingBranches || branchControlsBusy) return;

    try {
      setFetchingBranches(true);

      await SCMService.fetchBranches(projectId);

      showNotificationGlobal(
        "Branch refresh started. Waiting for branches to update.",
        "info",
        "bottomRight"
      );
    } catch (err) {
      showNotificationGlobal(
        err?.response?.data?.message || "Couldn't refresh branches.",
        "error",
        "bottomRight"
      );
      setFetchingBranches(false);
    }
  };


  // Disables the branch dropdown while ANY git job that cares about the
  // active branch is in flight, not just a switch — syncing or creating
  // a branch mid-switch would be confusing (which branch does the sync
  // even target?).
  

  return (
    <div style={Styles.page}>
      <style>{globalKeyframes}</style>

      <div style={Styles.topBar}>
        {onBack && (
          <button style={Styles.backButton} onClick={onBack}>
            <LucideIcons.ArrowLeft size={14} />
            Back to project
          </button>
        )}
        <span style={Styles.breadcrumb}>Connectors</span>
      </div>

      <div style={Styles.content}>
        <div style={Styles.pageHeader}>
          <div style={Styles.pageTitle}>Connectors</div>
          <div style={Styles.pageSubtitle}>
            {loading
              ? "Checking connection…"
              : isConnected
              ? "Your project syncs with a repository on GitHub."
              : isPending
              ? "Setting up your repository on GitHub."
              : "Sync this project's code with a repository on GitHub."}
          </div>
        </div>

        {!loading && error && (
          <div style={Styles.errorBanner}>
            <LucideIcons.AlertCircle size={15} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        {loading && (
          <div style={{ ...Styles.card, padding: 26 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={Styles.skeletonRow("55%")} />
              <div style={Styles.skeletonRow("35%")} />
              <div style={Styles.skeletonRow("75%")} />
            </div>
          </div>
        )}

        {/* ─── Connected state ──────────────────────────────────── */}

        {!loading && !error && isConnected && (
          <div style={{ ...Styles.card, animation: "sax-scm-card-in 0.2s ease" }}>
            <div style={{ ...Styles.section, paddingBottom: 20 }}>
              <div style={Styles.repoIdentity}>
                <div style={Styles.repoAvatarWrap}>
                  <div style={Styles.repoAvatar(avatarColor)}>{ownerInitial}</div>
                  <div style={Styles.avatarStatusDot(C.green)} title="Connected" />
                </div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={Styles.repoName}>
                      {repository?.fullName ||
                        `${repository?.owner || ""}/${repository?.repoName || ""}`}
                    </span>

                    {repository?.visibility && (
                      <span style={{ ...Styles.visibilityBadge, marginLeft: 0 }}>
                        {repository.visibility.toLowerCase()}
                      </span>
                    )}

                    <BranchDropdown
                      branches={branchList}
                      activeBranch={activeBranch}
                      onCreateBranch={openCreateBranchModal}
                      onSelectBranch={handleSwitchBranch}
                      switching={branchControlsBusy}
                    />
                  </div>

                  <div>
                    {repository?.url && (
                      <a
                        href={repository.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={Styles.externalLink}
                      >
                        View on GitHub
                        <LucideIcons.ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div style={Styles.sectionDivider} />

            <div style={Styles.section}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <StatusCell synced={isSynced && !syncing} branch={activeBranch} />
                  <div style={{ marginTop: 16 }}>
                    <div style={Styles.sectionLabel}>
                      Changes ({gitChangeCount})
                    </div>

                    {gitChanges.length === 0 ? (
                      <div style={Styles.cellText}>
                        No local changes
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                        {gitChanges.map((change, index) => (
                          <div
                            key={`${change.path}-${index}`}
                            style={{
                              padding: "10px 12px",
                              border: `1px solid ${C.border}`,
                              borderRadius: 8,
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <span style={Styles.cellText}>{change.path}</span>
                            <span style={{ fontSize: 12, color: C.secondary }}>
                              {change.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {syncSteps.length > 0 && (
                    <SyncProgressCell steps={syncSteps} />
                  )}
                  {!syncing && lastCheckedAt && (
                    <div style={Styles.syncTimestamp}>
                      Checked {formatRelativeTime(lastCheckedAt)}
                    </div>
                  )}
                </div>
                <button
                  style={Styles.iconGhostButton}
                  onClick={handleRecheck}
                  disabled={rechecking}
                  title="Re-check sync status"
                >
                  <LucideIcons.RefreshCw
                    size={12}
                    style={
                      rechecking
                        ? { animation: "sax-scm-spin 0.8s linear infinite" }
                        : undefined
                    }
                  />
                  Re-check
                </button>


                <button
                  type="button"
                  style={{
                    ...Styles.iconGhostButton,
                    opacity: fetchingBranches || branchControlsBusy ? 0.5 : 1,
                    cursor: fetchingBranches || branchControlsBusy
                      ? "not-allowed"
                      : "pointer",
                  }}
                  onClick={handleFetchBranches}
                  disabled={fetchingBranches || branchControlsBusy}
                  title="Refresh branches from GitHub"
                >
                  <LucideIcons.RefreshCw
                    size={12}
                    style={
                      fetchingBranches
                        ? { animation: "sax-scm-spin 0.8s linear infinite" }
                        : undefined
                    }
                  />
                  {fetchingBranches ? "Fetching…" : "Refresh branches"}
                </button>

              </div>
            </div>

            <div style={Styles.sectionDivider} />

            <div style={Styles.section}>
              <div style={Styles.sectionLabel}>Actions</div>
              <div style={Styles.actionsRow}>
                <Btn variant="primary" disabled={syncing} onClick={openCommitMessageModal}>
                  <LucideIcons.ArrowUpFromLine size={14} />
                  Sync changes
                </Btn>
              </div>
              <div style={Styles.actionHint}>
                Sync changes commits your latest code and sends it to {activeBranch} on
                GitHub.
              </div>
            </div>

            {hasCloneOptions && (
              <>
                <div style={Styles.sectionDivider} />
                <div style={Styles.section}>
                  <div style={Styles.sectionLabel}>Clone this repository</div>
                  <div style={Styles.tabRow}>
                    {cloneVariants.https && (
                      <button
                        style={Styles.tabButton(cloneTab === "https")}
                        onClick={() => setCloneTab("https")}
                      >
                        HTTPS
                      </button>
                    )}
                    {cloneVariants.ssh && (
                      <button
                        style={Styles.tabButton(cloneTab === "ssh")}
                        onClick={() => setCloneTab("ssh")}
                      >
                        SSH
                      </button>
                    )}
                    {cloneVariants.cli && (
                      <button
                        style={Styles.tabButton(cloneTab === "cli")}
                        onClick={() => setCloneTab("cli")}
                      >
                        GitHub CLI
                      </button>
                    )}
                  </div>
                  <CloneCell text={activeCloneText} />
                </div>
              </>
            )}
          </div>
        )}

        {/* ─── Provisioning state ───────────────────────────────── */}

        {!loading && !error && isPending && (
          <div style={{ ...Styles.card, animation: "sax-scm-card-in 0.2s ease", }}>
            <ConnectionLockup phase="pending" roomy />
            <div style={Styles.lockupCaption}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: C.ink }}>
                Setting up your repository…
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: C.secondary,
                  marginTop: 5,
                  lineHeight: 1.5,
                  maxWidth: 340,
                  margin: "5px auto 0",
                }}
              >
                Creating the repository on GitHub and pushing your project's
                code. This updates automatically once it's done.
              </div>

              <div style={{ marginTop: 14 }}>
                <button
                  style={Styles.textGhostButton}
                  onClick={handleRecheck}
                  disabled={rechecking}
                >
                  <LucideIcons.RefreshCw
                    size={12}
                    style={
                      rechecking
                        ? { animation: "sax-scm-spin 0.8s linear infinite" }
                        : undefined
                    }
                  />
                  Taking a while? Check now
                </button>
              </div>
            </div>

            <div style={{ height: 26 }} />
          </div>
        )}

        {/* ─── Not connected state ─────────────────────────────── */}

        {!loading && !error && showPicker && (
          <div style={{ ...Styles.card, animation: "sax-scm-card-in 0.2s ease" }}>
            <ConnectionLockup phase="disconnected" roomy={false} />
            <div style={{ ...Styles.lockupCaption, marginTop: 10, marginBottom: 4 }}>
              <span
                style={{
                  fontSize: 12.5,
                  color: mappingError ? C.coral : C.muted,
                  fontWeight: 500,
                }}
              >
                {mappingError ? "Connection failed" : "Not connected yet"}
              </span>
            </div>

            {mappingError && (
              <div style={{ ...Styles.errorBanner, margin: "12px 20px 20px" }}>
                <LucideIcons.AlertCircle
                  size={15}
                  style={{ marginTop: 1, flexShrink: 0 }}
                />
                <span>{mappingError}</span>
              </div>
            )}

            <div style={Styles.sectionDivider} />

            <div style={{ ...Styles.section, paddingTop: 22 }}>
              {githubApps.length === 0 ? (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>
                    No GitHub configurations yet
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: C.secondary,
                      marginTop: 5,
                      lineHeight: 1.6,
                      maxWidth: 420,
                    }}
                  >
                    Connect a GitHub account from Agent Apps first, then come back
                    here to link a repository.
                  </div>
                </div>
              ) : (
                <>
                  <div style={Styles.sectionLabel}>Choose a configured account</div>
                    <div>
                      {githubApps.map((app) => (
                        <GitHubConfigRow
                          key={app.id}
                          app={app}
                          selected={selectedApp?.id === app.id}
                          connecting={connecting}
                          onClick={() => setSelectedApp(app)}
                          onConnect={(a) => {
                            setSelectedApp(a);
                            handleConnect(a);
                          }}
                        />
                      ))}
                    </div>                 
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {isCreateBranchOpen && (
        <div
          style={Styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !creatingBranch) {
              closeCreateBranchModal();
            }
          }}
        >
          <div style={Styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={Styles.modalHeader}>
              <div>
                <h3 style={Styles.modalTitle}>Create new branch</h3>
                <p style={Styles.modalSubtitle}>
                  Create a new branch from the current repository.
                </p>
              </div>
              <button
                type="button"
                style={Styles.modalClose}
                onClick={closeCreateBranchModal}
                disabled={creatingBranch}
                aria-label="Close"
              >
                <LucideIcons.X size={18} />
              </button>
            </div>

            <div style={Styles.modalBody}>
              <label style={Styles.inputLabel}>Branch name</label>
              <input
                type="text"
                value={branchName}
                onChange={(e) => handleBranchNameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !creatingBranch) {
                    handleCreateBranch();
                  }
                }}
                placeholder="feature/my-new-branch"
                disabled={creatingBranch}
                autoFocus
                style={{
                  ...Styles.branchInput,
                  borderColor: branchNameError ? C.coral : C.borderStrong,
                }}
              />
              <p
                style={{
                  ...Styles.inputHint,
                  color: branchNameError ? C.coral : C.muted,
                }}
              >
                {branchNameError || "Enter a valid Git branch name."}
              </p>
              <div style={Styles.sourceRow}>
                <span style={Styles.sourceLabel}>Source</span>

                <span style={Styles.sourceBranch}>
                  <LucideIcons.GitBranch size={14} color={C.accent} />
                  {activeBranch}
                </span>
              </div>
            </div>

            <div style={Styles.modalFooter}>
              <Btn variant="neutral" onClick={closeCreateBranchModal} disabled={creatingBranch}>
                Cancel
              </Btn>
              <Btn
                variant="primary"
                onClick={handleCreateBranch}
                disabled={!branchName.trim() || !!branchNameError || creatingBranch}
              >
                {creatingBranch ? (
                  <>
                    <LucideIcons.Loader2
                      size={14}
                      color={C.accent}
                      style={{
                        animation:
                          "sax-scm-spin 0.8s linear infinite",
                      }}
                    />
                    Creating…
                  </>
                ) : (
                  <>
                    <LucideIcons.GitBranchPlus size={14} />
                    Create branch
                  </>
                )}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {showRenamePopup && (
        <div
          style={Styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !connecting) {
              setRepositoryName("");
              setShowRenamePopup(false);
            }
          }}
        >
          <div
            style={Styles.modal}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={Styles.modalHeader}>
              <div>
                <h3 style={Styles.modalTitle}>Rename Project</h3>

                <p style={Styles.modalSubtitle}>
                  Please give your project a name before connecting to GitHub.
                </p>
              </div>

              <button
                type="button"
                style={Styles.modalClose}
                onClick={() => {
                  if (!connecting) {
                    setRepositoryName("");
                    setShowRenamePopup(false);
                  }
                }}
                disabled={connecting}
                aria-label="Close"
              >
                <LucideIcons.X size={18} />
              </button>
            </div>

            <div style={Styles.modalBody}>
              <label style={Styles.inputLabel}>Project name</label>

              <input
                type="text"
                value={repositoryName}
                onChange={(e) => setRepositoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !connecting &&
                    repositoryName.trim()
                  ) {
                    handleRenameAndConnect();
                  }
                }}
                placeholder="Enter project name"
                disabled={connecting}
                autoFocus
                style={Styles.branchInput}
              />

              <p style={Styles.inputHint}>
                This name will be saved before connecting your project to GitHub.
              </p>
            </div>

            <div style={Styles.modalFooter}>
              <Btn
                variant="neutral"
                onClick={() => {
                  setRepositoryName("");
                  setShowRenamePopup(false);
                }}
                disabled={connecting}
              >
                Cancel
              </Btn>

              <Btn
                variant="primary"
                onClick={handleRenameAndConnect}
                disabled={!repositoryName.trim() || connecting}
              >
                {connecting ? (
                  <>
                    <LucideIcons.Loader2
                      size={14}
                      style={{
                        animation:
                          "sax-scm-spin 0.8s linear infinite",
                      }}
                    />
                    Renaming & connecting…
                  </>
                ) : (
                  <>
                    <LucideIcons.Github size={14} />
                    Rename & connect
                  </>
                )}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {isCommitMessageOpen && (
        <div
          style={Styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !syncing) {
              closeCommitMessageModal();
            }
          }}
        >
          <div style={Styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={Styles.modalHeader}>
              <div>
                <h3 style={Styles.modalTitle}>
                  Sync changes
                </h3>              
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    columnGap: 5,
                    rowGap: 4,
                    marginTop: 5,
                    fontSize: 13,
                    color: C.secondary,
                    lineHeight: 1.5,
                  }}
                >
                  <span>Changes will be synced to</span>

                  <span>current branch</span>

                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 9px",
                      borderRadius: 7,
                      background: C.accentTint,
                      border: `1px solid ${C.accentSoft}`,
                      color: C.accent,
                      fontFamily: MONO,
                      fontSize: 12.5,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <LucideIcons.GitBranch size={13} />
                    {activeBranch}
                  </span>
                </div>
              </div>
              <button
                type="button"
                style={Styles.modalClose}
                onClick={closeCommitMessageModal}
                disabled={syncing}
                aria-label="Close"
              >
                <LucideIcons.X size={18} />
              </button>
            </div>

            <div style={Styles.modalBody}>
              <label style={Styles.inputLabel}>Commit message</label>
              <input
                type="text"
                value={commitMessage}
                onChange={(e) => setCommitMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !syncing && commitMessage.trim()) {
                    handleSync();
                  }
                }}
                placeholder="Update project code"
                disabled={syncing}
                autoFocus
                style={Styles.branchInput}
              />
              <p style={Styles.inputHint}>This message will be used for the Git commit.</p>
            </div>

            <div style={Styles.modalFooter}>
              <Btn variant="neutral" onClick={closeCommitMessageModal} disabled={syncing}>
                Cancel
              </Btn>
              <Btn
                variant="primary"
                onClick={handleSync}
                disabled={!commitMessage.trim() || syncing}
              >
                {syncing ? (
                  <>
                    <LucideIcons.Loader2
                      size={14}
                      style={{ animation: "sax-scm-spin 0.8s linear infinite" }}
                    />
                    Syncing…
                  </>
                ) : (
                  <>
                    <LucideIcons.ArrowUpFromLine size={14} />
                    Sync changes
                  </>
                )}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SCMIntegrationPage;